import { Matrix } from '@mews/b2b-ui';
import { lt } from '@mews/framework';
import { Container, EmptyStateProps, Stack, ActionToolbar, ZIndex, Button, IconName } from '@optimus-web/core';
import { useCityLedgerMultipleInvoicesContext } from '../../context/CityLedgerMultipleInvoicesContext';
import { useColumns } from '../../hooks/useTableMapping';
import { AccountsTableData } from '../../types';
import { BulkActions } from './BulkActions';
import { CityLedgerPagination } from './CityLedgerPagination';

interface CityLedgerTableProps {
    isTableCollapsed: boolean;
    accounts: AccountsTableData[];
    error: Error | null;
    isLoading?: boolean;
    totalRowCount?: number;
}

export const CityLedgerTable: React.FC<CityLedgerTableProps> = ({
    isTableCollapsed = true,
    accounts,
    error,
    isLoading,
    totalRowCount,
}) => {
    const { selectedInvoices, setSelectedInvoices } = useCityLedgerMultipleInvoicesContext();
    const mapping = useColumns();

    const matrixProps = {
        source: accounts,
        mapping,
    };

    const emptyStateMessage: EmptyStateProps = error
        ? { primaryText: error.message, pictogram: 'system_error' }
        : { primaryText: lt('NoDataAvailable') };

    return (
        <Stack vertical spacing="200" fill data-test-id="city-ledger-table">
            <Container position="sticky" top="0" zIndex={ZIndex.Highest}>
                {selectedInvoices.length > 0 && (
                    <Container data-test-id="city-ledger-table-bulk-actions">
                        <ActionToolbar
                            leftSide={
                                <Stack spacing="100" itemAlignment="center">
                                    {lt('CityLedgerItemsSelected', { count: selectedInvoices.length })}
                                    <Button
                                        variant="primary"
                                        size="small"
                                        onClick={() => {
                                            setSelectedInvoices([]);
                                        }}
                                        type="button"
                                        icon={IconName.ClearSelection}
                                    >
                                        {lt('ClearSelection')}
                                    </Button>
                                </Stack>
                            }
                            rightSide={<BulkActions />}
                        />
                    </Container>
                )}
            </Container>
            <Matrix
                source={matrixProps.source}
                mapping={matrixProps.mapping}
                fixedHeader
                bordered
                collapseAll={isTableCollapsed}
                emptyState={error || accounts?.length === 0 ? emptyStateMessage : undefined}
                loading={!!isLoading}
            />
            <CityLedgerPagination totalRowCount={totalRowCount || 0} />
        </Stack>
    );
};
